import { Alert, AppBar, Box, Button, CircularProgress, Divider, Paper, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../../services/HttpService';
import { InfoArea } from '../../../../components/ui/InfoArea';
import { User, UserContact, UserAccessgroup, newUserAccessgroup, newUserOrganization, UserOrganization, newUserCompany, UserCompany, newUserContact, Contact } from '../../../../model/User';
import { UserForm, UserFormRef } from './UserForm';
import { TabPanel, TabProps } from '../../../../components/ui/TabPanel';
import { DataInfoTable, DataListInfoTable } from '../../../../components/tables/DataTable';
import { Accessgroup } from '../../../../model/System';
import { Company, Organization } from '../../../../model';
import { ManyToManyForm, ManyToManyFormRef } from '../ManyToManyForm';
import { dayjsService } from '../../../../services/dayjsService';
import { CenterSpinner } from '../../../../components/ui/CenterSpinner';

interface UserOverviewProps {
  item: User;
  getError?: string;
  onClose?: () => void;
  onItemChange?: (user: User) => void;
}

export const UserOverview = (props: UserOverviewProps) => {
  const theme = useTheme();
  const { getError, onItemChange } = props;
  const { api, error, isLoading } = useApi();
  const { api: modApi, error: modError, isLoading: modIsLoading } = useApi();
  const [item, setItem] = useState<User>(props.item);
  const [userContact, setUserContact] = useState<UserContact>(newUserContact);
  const [userAccessgroup, setUserAccessgroup] = useState<UserAccessgroup>(newUserAccessgroup);
  const [userCompany, setUserCompany] = useState<UserCompany>(newUserCompany);
  const [userOrganization, setUserOrganization] = useState<UserOrganization>(newUserOrganization);
  const [displayData, setDisplayData] = useState<Record<string, string>>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const addUserRef = useRef<UserFormRef>(null);
  const addUserContactRef = useRef<ManyToManyFormRef>(null);
  const addUserAccessgroupRef = useRef<ManyToManyFormRef>(null);
  const addUserCompanyRef = useRef<ManyToManyFormRef>(null);
  const addUserOrganizationRef = useRef<ManyToManyFormRef>(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!props.item || props.item.id === 0) {
      setTabIndex(1);
    } else {
      getItem();
      setDisplayData({
        "Fornavn": props.item.first_name,
        "Etternavn": props.item.last_name,
        "Fult Navn": props.item.name,
        "Brukernavn": props.item.username,
        "Passord utløper": props.item.pw_expiredate ? dayjsService.toDateDisplayString(props.item.pw_expiredate) : "-",
        "e-post": props.item.emailaddress,
        "e-post verifisert": props.item.email_verified ? "Ja" : "Nei",
        "Telefon": props.item.telephone,
        "Telefon verifisert": props.item.phone_verified ? "Ja" : "Nei",
        "OIDC identifikator": props.item.oidc_id,
      })
    }
  }, [props.item]);

  const getItem = () => {
    api("/getOidcUser", "POST", { user_id: props.item.id, with_relations: true }).then((res) => {
      if (res.statusCode === 200) {
        setItem(res.user);
      }
    });
  }

  const reset = () => {
    setItem(() => ({ ...item, password: "" })); // triggers props.user change in UserForm
  }

  const changeTab = (index: number, performChange: boolean = true): boolean => {
    if (!item.id) {
      if (performChange) {
        setTabIndex(1);
      }

      return index === 1;
    }

    if (performChange) {
      setTabIndex(index);
    }

    return true;
  }

  const saveUser = () => {
    const userRef = addUserRef.current.getUser();
    console.log("submitting", userRef, "valid", addUserRef.current.valid());

    if (addUserRef.current.valid()) {
      api("/manage/user/postUser", "POST", { ...userRef }).then(res => {
        console.log("res:", res)
        if (res?.statusCode === 200) {
          onItemChange(res.user);
          setTabIndex(0);
        }
      });
    }
  }

  const saveUserContact = () => {
    const newRel = addUserContactRef.current.getItem();
    console.log("item", newRel, "valid", addUserContactRef.current.valid());

    if (addUserContactRef.current.valid()) {
      const isNew = userContact.id === 0;

      const updatedContact = {
        ...userContact,
        oidc_user_id: item.id,
        contact_id: newRel.select_id,
        from_date: newRel.from_date,
        to_date: newRel.to_date
      };

      api("/manage/user/postUserContact", "POST", updatedContact).then(res => {
        if (res?.statusCode === 200) {
          setUserContact(res.user_contact);

          setItem((prev) => ({
            ...prev,
            user_contacts: isNew
              ? [...(prev.user_contacts || []), res.user_contact]
              : prev.user_contacts.map(uContact =>
                uContact.id === updatedContact.id ? res.user_contact : uContact
              ),
          }));
        }
      });
    }
  }

  const saveUserAccessgroup = () => {
    const newRel = addUserAccessgroupRef.current.getItem();
    console.log("item", newRel, "valid", addUserAccessgroupRef.current.valid());

    if (addUserAccessgroupRef.current.valid()) {
      const isNew = userAccessgroup.id === 0;

      const updatedAccessgroup = {
        ...userAccessgroup,
        oidc_user_id: item.id,
        accessgroup_id: newRel.select_id,
        from_date: newRel.from_date,
        to_date: newRel.to_date
      };

      api("/manage/user/postUserAccessgroup", "POST", updatedAccessgroup).then(res => {
        if (res?.statusCode === 200) {
          setUserAccessgroup(res.user_accessgroup);

          setItem((prev) => ({
            ...prev,
            user_accessgroups: isNew
              ? [...(prev.user_accessgroups || []), res.user_accessgroup]
              : prev.user_accessgroups.map(uAccessgroup =>
                uAccessgroup.id === updatedAccessgroup.id ? res.user_contact : uAccessgroup
              ),
          }));
        }
      });
    }
  }

  const saveUserCompany = () => {
    const newRel = addUserCompanyRef.current.getItem();
    console.log("item", newRel, "valid", addUserCompanyRef.current.valid());

    if (addUserCompanyRef.current.valid()) {
      const isNew = userCompany.id === 0;

      const updatedCompany = {
        ...userCompany,
        oidc_user_id: item.id,
        company_id: newRel.select_id,
        from_date: newRel.from_date,
        to_date: newRel.to_date
      };

      api("/manage/user/postUserCompany", "POST", updatedCompany).then(res => {
        if (res?.statusCode === 200) {
          setUserCompany(res.user_company);

          setItem((prev) => ({
            ...prev,
            user_companies: isNew
              ? [...(prev.user_companies || []), res.user_company]
              : prev.user_companies.map(uCompany =>
                uCompany.id === updatedCompany.id ? res.user_company : uCompany
              ),
          }));
        }
      });
    }
  }

  const saveUserOrganization = () => {
    const newRel = addUserOrganizationRef.current.getItem();
    console.log("item", newRel, "valid", addUserOrganizationRef.current.valid());

    if (addUserOrganizationRef.current.valid()) {
      const isNew = userOrganization.id === 0;

      const updatedOrganization = {
        ...userOrganization,
        oidc_user_id: item.id,
        organization_id: newRel.select_id,
        from_date: newRel.from_date,
        to_date: newRel.to_date
      };

      api("/manage/user/postUserOrganization", "POST", updatedOrganization).then(res => {
        if (res?.statusCode === 200) {
          setUserOrganization(res.user_organization);

          setItem((prev) => ({
            ...prev,
            user_organizations: isNew
              ? [...(prev.user_organizations || []), res.user_organization]
              : prev.user_organizations.map(uOrganization =>
                uOrganization.id === updatedOrganization.id ? res.user_organization : uOrganization
              ),
          }));
        }
      });
    }
  }

  return (
    <>
      <AppBar position="static"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? "inherit" : theme.palette.grey[300],
          color: theme.palette.mode === 'dark' ? "white" : "black"
        }}
      >
        {error && <Alert severity='error'>{error}</Alert>}
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant={"scrollable"}
          textColor="inherit"
          indicatorColor="primary"
          aria-label="manage user tabs"
        >
          {[
            "Oversikt",
            item.id ? "Rediger" : "Opprett", ,
            `Kontaktpersonerer (${item.user_contacts?.length ?? 0})`,
            `Tilgangsgrupper (${item.user_accessgroups?.length ?? 0})`,
            `Bedrifter (${item.user_companies?.length ?? 0})`,
            `Organisasjoner (${item.user_organizations?.length ?? 0})`
          ].map((name, index) => (
            <Tab disabled={!changeTab(index, false)} key={index} label={name} sx={{ fontWeight: "bold", textTransform: "none" }} {...TabProps(index)} />
          ))}
        </Tabs>
      </AppBar>
      <Box sx={{ mt: 2, p: { xs: 0, md: 3 }, minHeight: 400 }}>
        <TabPanel value={tabIndex} index={0} sx={{ minHeight: 200 }}>
          <Box sx={{ display: 'flex', flexDirection: "column", justifyContent: 'center', gap: 3, maxWidth: { xs: "100%", md: 1200 } }}>
            <DataInfoTable data={displayData} onEdit={() => changeTab(1)} />
            <CenterSpinner loading={isLoading}>
              <>
                <DataListInfoTable title={"Kontaktpersoner"}
                  data={item.user_contacts ?? []}
                  displayField="contact_name"
                  onAdd={() => { setUserContact(newUserContact()); changeTab(2) }}
                  onEdit={(id) => { setUserContact(item.user_contacts.find(u => u.id === id)); changeTab(2) }}
                  onDelete={(id) => alert("delete not implemented")}
                />
                <DataListInfoTable title={"Tilgangsgrupper"}
                  data={item.user_accessgroups ?? []}
                  displayField="accessgroup_name"
                  onAdd={() => { setUserAccessgroup(newUserAccessgroup()); changeTab(3) }}
                  onEdit={(id) => { setUserAccessgroup(item.user_accessgroups.find(u => u.id === id)); changeTab(3) }}
                  onDelete={(id) => alert("delete not implemented")}
                />
                <DataListInfoTable title={"Bedrifter"}
                  data={item.user_companies ?? []}
                  displayField="company_name"
                  onAdd={() => { setUserCompany(newUserCompany()); changeTab(4) }}
                  onEdit={(id) => { setUserCompany(item.user_companies.find(u => u.id === id)); changeTab(4) }}
                />
                <DataListInfoTable title={"Organisasjoner"}
                  data={item.user_organizations ?? []}
                  displayField="organization_name"
                  onAdd={() => { setUserOrganization(newUserOrganization()); changeTab(5) }}
                  onEdit={(id) => { setUserOrganization(item.user_organizations.find(u => u.id === id)); changeTab(5) }}
                />
              </>
            </CenterSpinner>
            {props.onClose && (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', p: 2 }}>
                <Button variant='contained' color='darkgrey' onClick={props.onClose}>Lukk</Button>
              </Box>
            )}
          </Box>
        </TabPanel>
        <TabPanel value={tabIndex} index={1} sx={{ minHeight: 200 }}>
          <Box sx={{ p: { xs: 1, md: 3 }, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: 1200 }}>
              <UserForm user={item} error={getError} ref={addUserRef} />
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button variant='contained' color='darkgrey' onClick={reset}>Tilbakestill</Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Button
                    variant='contained'
                    color='darkgrey'
                    onClick={() => {
                      if (changeTab(0, false)) {
                        changeTab(0);
                      } else if (props.onClose) {
                        props.onClose();
                      }
                    }}
                  >
                    Avbryt
                  </Button>
                  <Button
                    variant='contained'
                    color='info'
                    onClick={saveUser}
                    disabled={isLoading}
                    endIcon={isLoading && <CircularProgress size={22} />}
                  >
                    {item.id ? "Lagre" : "Opprett"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>
        <TabPanel value={tabIndex} index={2} sx={{ minHeight: 200 }}>
          <>
            <ManyToManyForm
              item={{
                select_id: userContact.contact_id,
                from_date: userContact.from_date,
                to_date: userContact.to_date
              }}
              selectItemName="Kontaktperson"
              selectTable="contact"
              ref={addUserContactRef}
              fromAndToDate
            />
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Button variant='contained' color='darkgrey' onClick={() => { changeTab(0); setUserContact(newUserContact()) }}>Avbryt</Button>
              <Button
                variant='contained'
                color='info'
                onClick={saveUserContact}
                disabled={isLoading}
                endIcon={isLoading && <CircularProgress size={22} />}
              >
                {userContact.id ? "Lagre" : "Opprett"}
              </Button>
            </Box>
            <DataListInfoTable title={"Kontaktpersoner"}
              data={item.user_contacts ?? []}
              displayField="contact_name"
              onAdd={() => { setUserContact(newUserContact()) }}
              onEdit={(id) => { setUserContact(item.user_contacts.find(u => u.id === id)) }}
            />
          </>
        </TabPanel>
        <TabPanel value={tabIndex} index={3} sx={{ minHeight: 200 }}>
          <>
            <ManyToManyForm
              item={{
                select_id: userAccessgroup.accessgroup_id,
                from_date: userAccessgroup.from_date,
                to_date: userAccessgroup.to_date
              }}
              selectItemName="Tilgangsgruppe"
              selectTable="accessgroup"
              ref={addUserAccessgroupRef}
              fromAndToDate
            />
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Button variant='contained' color='darkgrey' onClick={() => { changeTab(0); setUserAccessgroup(newUserAccessgroup()) }}>Avbryt</Button>
              <Button
                variant='contained'
                color='info'
                onClick={saveUserAccessgroup}
                disabled={isLoading}
                endIcon={isLoading && <CircularProgress size={22} />}
              >
                {userContact.id ? "Lagre" : "Opprett"}
              </Button>
            </Box>
            <DataListInfoTable title={"Tilgangsgrupper"}
              data={item.user_accessgroups ?? []}
              displayField="accessgroup_name"
              onAdd={() => setUserAccessgroup(newUserAccessgroup())}
              onEdit={(id) => setUserAccessgroup(item.user_accessgroups.find(u => u.id === id))}
            />
          </>
        </TabPanel>
        <TabPanel value={tabIndex} index={4} sx={{ minHeight: 200 }}>
          <>
            <ManyToManyForm
              item={{
                select_id: userCompany.company_id,
                from_date: userCompany.from_date,
                to_date: userCompany.to_date
              }}
              selectItemName="Bedrift"
              selectTable="company"
              ref={addUserCompanyRef}
              fromAndToDate
            />
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Button variant='contained' color='darkgrey' onClick={() => { changeTab(0); setUserCompany(newUserCompany()) }}>Avbryt</Button>
              <Button
                variant='contained'
                color='info'
                onClick={saveUserCompany}
                disabled={isLoading}
                endIcon={isLoading && <CircularProgress size={22} />}
              >
                {userCompany.id ? "Lagre" : "Opprett"}
              </Button>
            </Box>
            <DataListInfoTable title={"Bedrifter"}
              data={item.user_companies ?? []}
              displayField="commpany_name"
              onAdd={() => { setUserCompany(newUserCompany()) }}
              onEdit={(id) => { setUserCompany(item.user_companies.find(u => u.id === id)) }}
            />
          </>
        </TabPanel>
        <TabPanel value={tabIndex} index={5} sx={{ minHeight: 200 }}>
          <>
            <ManyToManyForm
              item={{
                select_id: userOrganization.organization_id,
                from_date: userOrganization.from_date,
                to_date: userOrganization.to_date
              }}
              selectItemName="Organisasjon"
              selectTable="organization"
              ref={addUserOrganizationRef}
              fromAndToDate
            />
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Button variant='contained' color='darkgrey' onClick={() => { changeTab(0); setUserOrganization(newUserOrganization()) }}>Avbryt</Button>
              <Button
                variant='contained'
                color='info'
                onClick={saveUserOrganization}
                disabled={isLoading}
                endIcon={isLoading && <CircularProgress size={22} />}
              >
                {userOrganization.id ? "Lagre" : "Opprett"}
              </Button>
            </Box>
            <DataListInfoTable title={"Organisasjoner"}
              data={item.user_organizations ?? []}
              displayField="organization_name"
              onAdd={() => { setUserOrganization(newUserOrganization()) }}
              onEdit={(id) => { setUserOrganization(item.user_organizations.find(u => u.id === id)) }}
            />
          </>
        </TabPanel>
      </Box>
    </>
  )
}